import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';

import { Grid } from '@material-ui/core';

import { FetchError } from '../../../common/api/api';
import AppSnackbar from '../../../common/AppSnackbar';
import TaskAttachmentCard from '../../tasks/cards/TaskAttachmentCard';
import { Task } from '../../tasks/Task.interface';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import DesignOutputDesignInputReferenceCard from '../cards/DesignOutputDesignInputReferenceCard';
import DesignOutputInfoCard from '../cards/DesignOutputInfoCard';
import DesignOutputUserNeedReferenceCard from '../cards/DesignOutputUserNeedReferenceCard';
import DesignOutputVerificationReferenceCard from '../cards/DesignOutputVerificationReferenceCard';
import {
  useFindProduct,
  useFindProductDesignOutputs,
  useProductFilterQueryParams
} from '../Product.hooks';
import {
  BaseProductDesignOutputCardProps,
  ProductDesignOutput,
  ProductEntity
} from '../Product.interface';
import ProductDocumentCommonPage from './ProductDocumentCommonPage';
import { getProductEntityName } from '../Product.config';

const ProductDesignOutputDocumentPage: React.FC = () => {
  const { productId: paramProductId = '' } = useParams<{
    productId?: string;
  }>();
  const productId: number = parseInt(paramProductId) || 0;
  const fetchUserNeedsRef = useRef<Function | undefined>();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const [search = '', setSearch] = useQueryParam('search', StringParam);
  const [productDesignOutputs, setProductDesignOutputs] = useState<
    ProductDesignOutput[]
  >([]);
  const {
    productDesignOutputList,
    loadingProductDesignOutputsError,
    findProductDesignOutputs
  } = useFindProductDesignOutputs(productId);
  const { product } = useFindProduct(productId, true);
  const { getParams } = useProductFilterQueryParams();

  const createCardProps = (
    productEntity: ProductEntity
  ): BaseProductDesignOutputCardProps => {
    const designOutput: ProductDesignOutput = productEntity as ProductDesignOutput;
    return {
      productId,
      productDesignOutput: designOutput,
      onUpdate: () => setDidUpdate(true),
      onError: (error: FetchError) => setError(error),
      onChange: (newProductDesignOutput: Partial<ProductDesignOutput>) => {
        const selectedIndex: number = productDesignOutputs.findIndex(
          (un: ProductDesignOutput) => un.id === designOutput.id
        );
        if (selectedIndex > -1) {
          const designOutputs: ProductDesignOutput[] = [
            ...productDesignOutputs
          ];
          designOutputs[selectedIndex] = {
            ...designOutput,
            ...newProductDesignOutput
          };
          setProductDesignOutputs(designOutputs);
        }
      }
    };
  };

  useEffect(() => {
    findProductDesignOutputs({ search, ...getParams() });
  }, [findProductDesignOutputs, search, getParams]);

  useEffect(() => {
    if (productDesignOutputList) {
      setProductDesignOutputs(productDesignOutputList.data);
    }
  }, [productDesignOutputList]);

  useEffect(() => {
    setError(loadingProductDesignOutputsError);
  }, [loadingProductDesignOutputsError]);

  return (
    <>
      <ProductDocumentCommonPage
        product={product}
        productEntities={productDesignOutputs}
        entity={ProductBoardColumnEntity.DesignOutput}
        renderInfoCard={(
          productEntity: ProductEntity,
          title: string,
          hidden?: boolean
        ) => (
          <DesignOutputInfoCard
            {...createCardProps(productEntity)}
            title={title}
            hidden={hidden}
            enableTitleRedirect
          />
        )}
        renderGridCards={(productEntity: ProductEntity) => {
          const props: BaseProductDesignOutputCardProps = createCardProps(
            productEntity
          );
          return (
            <>
              <Grid item xs={12}>
                <DesignOutputUserNeedReferenceCard
                  {...props}
                  fetchRef={fetchUserNeedsRef}
                />
              </Grid>
              <Grid item xs={12}>
                <DesignOutputDesignInputReferenceCard
                  {...props}
                  onUpdate={() => {
                    fetchUserNeedsRef.current?.();
                    props.onUpdate?.();
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <DesignOutputVerificationReferenceCard {...props} />
              </Grid>
              {productEntity.task && (
                <Grid item xs={12}>
                  <TaskAttachmentCard
                    compact
                    task={productEntity.task}
                    onError={props.onError}
                    onChange={(newTask: Partial<Task>) => {
                      Object.assign(productEntity.task, newTask);
                      // update with new task
                      props.onChange?.({});
                      props.onUpdate?.();
                    }}
                  />
                </Grid>
              )}
            </>
          );
        }}
      />

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Product {getProductEntityName('designOutput')} details updated.
      </AppSnackbar>
    </>
  );
};

export default ProductDesignOutputDocumentPage;
